/* eslint-disable eqeqeq */
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { USPSNavBar } from "../common/components/nav-bar/USPSNavBar";
import { PageNotFound } from "../common/components/PageNotFound";
import { ScrollToTop } from "../common/components/ScrollToTop";
import { USPSGlobalFooter } from "../common/components/SMARTLOCKER-footer";
import { USPSGlobalHeader } from "../common/components/SMARTLOCKER-header";
import {
    BUSINESS_PAGE_URL,
    CUSTOMER_PAGE_URL,
    FAQ_PAGE_URL,
    LOCKER_LOCATION_PAGE_URL,
    MERCHANT_PAGE_URL,
    REDIRECT_URL,
    RETAILERS_URL,
} from "../config/pages";
import Business from "../features/business/Business";
import PackageRecipients from "../features/customer/PackageRecipients";
import Faq from "../features/faq/Faq";
import Landing from "../features/landing/Landing";
import CSVLockerLookup from "../features/locker-lookup/CSVLockerLookup";
import { LockerLookup } from "../features/locker-lookup/LockerLookup";
import CurrentAndPastReservations from "../features/locker-reservation/CurrentAndPastReservations";
import GuestReserve from "../features/locker-reservation/GuestReserve";
import GuestReserveCode from "../features/locker-reservation/GuestReserveCode";
import { PackageShippers } from "../features/merchant/PackageShippers";
import { Api } from "../services/Api";
import Retailers from "../features/retailers/retailers";
import LockerRedirect from "../features/locker-redirect/LockerRedirect";

const ProtectedRouteProps = {
    isAllowed: PropTypes.bool,
    redirectPath: PropTypes.string,
    children: PropTypes.node,
};

const ProtectedRoute = ({ isAllowed, redirectPath = "/login", children }) => {
    if (!isAllowed) {
        return <Navigate to={redirectPath} replace />;
    }

    return children || <Outlet />;
};

ProtectedRoute.propTypes = ProtectedRouteProps;

const App = () => {
    const queryString = window.location.search;
    const api = new Api();
    api.setup();

    const [tradeShow, setTradeShow] = useState();
    const [csvFlag, setCSVFlag] = useState();
    const [psFlag, setPSFlag] = useState();
    const [lockerList, setLockerList] = useState();
    const [contestLink, setContestLink] = useState(false);
    const [loading, setLoading] = useState(true);

    const location = useLocation();
    const content = useRef(null);
    const navbar = useRef(null);
    const getAllTradeShows = useCallback(async () => {
        return await api
            .getAllTradeShows()
            // .then(response => tradeShowCheck(response.data, "show-id", Number(localStorage.getItem("show_id"))));
            .then(response => response.data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getFeatureFlag = useCallback(async featureFlag => {
        return await api.getFeatureFlag(featureFlag);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCurrentLockerList = useCallback(async () => {
        return await api.getCurrentLockerList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            const tradeShowNew = await getAllTradeShows();
            const currentTradeShow = tradeShowNew?.filter(tradeShow => tradeShow["contest-flag"] === true);
            // Look for whether new CSV search is being used or not
            setTradeShow(currentTradeShow?.[0]);
            const csvResponse = await getFeatureFlag("csv-search");
            setCSVFlag(csvResponse.data.response);
            const psResponse = await getFeatureFlag("hide-ps");
            setPSFlag(psResponse.data.response);
            setLockerList(await getCurrentLockerList());
            setLoading(false);
        })();
    }, [getAllTradeShows, getFeatureFlag, getCurrentLockerList]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const resizeContent = useCallback(() => {
        if (!content.current || !navbar.current) return;
        content.current.style.marginTop = `${navbar.current.getBoundingClientRect().height}px`;
    });

    useEffect(() => {
        resizeContent();
    }, [resizeContent]);

    useEffect(() => {
        window.addEventListener("resize", resizeContent);
        return () => window.removeEventListener("resize", resizeContent);
    }, [resizeContent]);

    useEffect(() => {
        if (location.pathname !== "/authentication/callback") {
            ReactGA.send({ hitType: "pageview", page: location.pathname });
        }
    }, [location]);

    useEffect(() => {
        if (queryString) {
            const urlParams = new URLSearchParams(queryString);

            localStorage.setItem("show_id", urlParams.get("show_id"));
            setContestLink(true);
        }

        if (localStorage.getItem("show_id") && !contestLink) setContestLink(true);
    }, [contestLink, queryString]);

    const reload = () => window.location.reload();

    const [ignore, setIgnore] = useState(false);

    const logAnalytics = async () => {
        return await api.postLogAnalytics();
    };

    useEffect(() => {
        if (!ignore) {
            logAnalytics();
            setIgnore(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ignore]);

    if (!loading) {
        return (
            <ScrollToTop>
                <Routes>
                    <Route element={<Outlet />}>
                        <Route exact path="/robots.txt" onEnter={reload} />
                    </Route>
                    <Route
                        element={
                            <>
                                <USPSGlobalHeader tradeShow={tradeShow} psFlag={psFlag} />
                                <USPSNavBar ref={navbar} tradeShow={tradeShow} psFlag={psFlag} />
                                <Outlet />
                                <USPSGlobalFooter />
                            </>
                        }
                    >
                        <Route path="/" element={<Landing />} />
                        {psFlag === "true" ? <></> : <Route path={MERCHANT_PAGE_URL} element={<PackageShippers />} />}
                        {/* <Route path={XCHANGE_PAGE_URL} element={<PackageShippers />} /> */}
                        <Route path={CUSTOMER_PAGE_URL} element={<PackageRecipients />} />
                        <Route path={BUSINESS_PAGE_URL} element={<Business />} />
                        <Route
                            path={LOCKER_LOCATION_PAGE_URL}
                            element={
                                csvFlag === "true" ? <CSVLockerLookup lockerList={lockerList} /> : <LockerLookup />
                            }
                        />
                        <Route path={FAQ_PAGE_URL} element={<Faq />} />
                        <Route path={RETAILERS_URL} element={<Retailers />} />
                        {process.env.REACT_APP_GUEST_RESERVATION === "true" && (
                            <Route path="guest-reservation">
                                <Route path="" element={<PageNotFound />} />
                                <Route path="email" element={<GuestReserve />} />
                                <Route path="code" element={<GuestReserveCode />} />
                                <Route path="success" element={<CurrentAndPastReservations />} />
                                <Route path="*" element={<PageNotFound />} />
                            </Route>
                        )}
                        <Route path="*" element={<PageNotFound />} />
                    </Route>
                    <Route element={<Outlet />}>
                        <Route path={REDIRECT_URL} element={<LockerRedirect lockerList={lockerList} />} />
                    </Route>
                </Routes>
            </ScrollToTop>
        );
    }
};

export default App;
